<template>
  <transition name="fade">
    <div v-if="isSidecarOpen" class="knowledge-base-sidecar-overlay" @click.self="close">
      <div class="knowledge-base-sidecar-panel">
        <div class="panel-header">
          <div class="is-align-items-center is-inline-flex is-mx-auto">
            <span class="tag is-primary mr-2">{{ $t('beta') }}</span>
            <h3>{{ $t('knowledgeBase.library.title') }}</h3>
          </div>
          <button class="delete" :disabled="isLocked" @click="close"></button>
        </div>
        <div class="panel-body" ref="conversationContainer">
          <div v-if="conversation.length === 0" class="conversation-area-placeholder">
            <p>{{ $t('knowledgeBase.library.promptDefault') }}</p>
          </div>
          <div v-else class="conversation-container">
            <div v-for="message in conversation"
                 class="message-wrapper"
                 :class="`is-${message.sender}`"
                 :key="message.id">
              <div v-if="message.sender === 'user'" class="message-content">
                {{ message.text }}
              </div>
              <ai-response-message v-else :message="message" @content-updated="scrollToBottom" />
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="field is-grouped">
            <div class="field is-expanded mb-0">
              <div class="control">
              <textarea v-model="userInput"
                        class="textarea is-primary"
                        maxlength="150"
                        :class="{ 'has-text-danger': !isQueryable }"
                        :disabled="isLocked || !isQueryable"
                        :placeholder="placeholderText"
                        @keydown.enter.prevent="handleSendMessage"></textarea>
                <div class="char-counter">{{ userInput.length }} / 150</div>
              </div>
              <p class="help">{{ $t('knowledgeBase.library.disclaimer') }}</p>
            </div>
            <div class="control">
              <button class="button is-primary"
                      :disabled="isLocked || !userInput.trim() || !isQueryable"
                      @click="handleSendMessage">
                <span class="icon is-small">
                  <i class="fas fa-paper-plane"></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import AiResponseMessage from './AiResponseMessage.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'KnowledgeBaseSidecar',
  components: {
    AiResponseMessage
  },
  data() {
    return {
      userInput: ''
    }
  },
  computed: {
    ...mapGetters(
      'knowledgeBase',
      ['conversation', 'isLocked', 'isSidecarOpen', 'isQueryable', 'statusMessage']
    ),
    placeholderText() {
      if (!this.isQueryable) {
        return this.$t('knowledgeBase.errors.maxTokensExceeded')
      }
      return this.$t('knowledgeBase.library.inputPlaceholder')
    }
  },
  watch: {
    isSidecarOpen(newValue) {
      if (newValue) {
        this.scrollToBottom()
      }
    }
  },
  methods: {
    ...mapActions('knowledgeBase', ['closeSidecar', 'sendQuery']),
    close() {
      if (!this.isLocked) {
        this.closeSidecar()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.conversationContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    handleSendMessage() {
      if (!this.userInput.trim()) return
      this.sendQuery(this.userInput)
      this.userInput = ''
      this.scrollToBottom()
    }
  }
}
</script>

<style scoped lang="scss">
.knowledge-base-sidecar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  z-index: auto;
}

@media (min-width: 1024px) {
  .knowledge-base-sidecar-overlay {
    background-color: transparent;
    pointer-events: none;
    position: static;
  }

  .knowledge-base-sidecar-panel {
    pointer-events: auto;
  }
}

.knowledge-base-sidecar-panel {
  background-color: white;
  box-shadow: -2px 0 5px rgba(0,0,0,0.1);
  flex-direction: column;
  display: flex;
  height: 100%;
  min-width: 400px;
  width: 30vw;

  .panel-header {
    align-items: center;
    border-bottom: 1px solid #dbdbdb;
    display: flex;
    justify-content: center;
    padding: 1rem;

    .is-mx-auto {
      margin-left: auto;
      margin-right: auto;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: bold;
    }
  }

  .panel-body {
    flex-grow: 1;
    margin: 1rem 0.5rem;
    padding: 1rem;
    overflow-y: auto;
    position: relative;
    display: flex;
    flex-direction: column;

    .conversation-area-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .conversation-container {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .message-wrapper {
      display: table;
      width: 80%;
    }

    .message-content {
      border-radius: 1.25rem;
      overflow-wrap: break-word;
      padding: 0.75rem 1rem;
      word-break: break-word;
      word-wrap: break-word;
    }

    .is-user {
      align-self: flex-end;
      justify-content: flex-end;
      .message-content {
        background-color: var(--primary);
        color: var(--primary--color-invert);
        border-bottom-right-radius: 0.25rem;
      }
    }

    .is-ai {
      align-self: flex-start;
      justify-content: flex-start;
    }
  }

  .panel-footer {
    border-top: 1px solid #dbdbdb;
    flex-shrink: 0;
    padding: 1rem;

    .control.is-expanded {
      position: relative;
    }

    .field.is-grouped {
      align-items: center;
    }

    .textarea {
      border-width: 2px;
      height: 60px;
      padding-right: 55px;
      resize: none;
    }

    .textarea.has-text-danger::placeholder {
      color: var(--danger);
    }

    .char-counter {
      position: absolute;
      bottom: 8px;
      right: 12px;
      font-size: 0.8rem;
      color: #b5b5b5;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .knowledge-base-sidecar-panel,
.fade-leave-active .knowledge-base-sidecar-panel {
  transition: transform 0.3s ease;
}

.fade-enter-from .knowledge-base-sidecar-panel,
.fade-leave-to .knowledge-base-sidecar-panel {
  transform: translateX(100%);
}
</style>
